import React, { useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
} from '@material-ui/core';
import { authSlice } from '../Store/modules/AuthModule/authSlice';
import { UploadOrgLogo } from '../Pages/Dashboard/apis';

const { actions } = authSlice;

export default function OrgLogoUploadModal({ open, onClose }) {
    const dispatch = useDispatch();
    const { user, orgLogos } = useSelector(({ auth }) => auth, shallowEqual);

    const inputRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (evt) => {
        const selected = evt.target.files[0];
        if (!selected) return;

        const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowed.includes(selected.type)) {
            setError('Only JPG and PNG files are allowed.');
            return;
        }

        setError('');
        setFile(selected);
        setPreview(URL.createObjectURL(selected));
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        setError('');
        try {
            const res = await UploadOrgLogo(user.organization, file);
            const logoUrl = res?.organization?.logo || res.logoUrl || res.logo || res.url;
            dispatch(actions.updateOrgLogo({ orgId: user.organization, logoUrl }));
            handleClose();
        } catch (ex) {
            setError(ex.response?.data?.message || ex.message || 'Upload failed.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreview(null);
        setError('');
        if (inputRef.current) inputRef.current.value = null;
        onClose();
    };

    const currentLogo = orgLogos?.[user?.organization];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Typography variant="h6">Organization Logo</Typography>
            </DialogTitle>
            <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 150,
                        height: 150,
                        border: '2px dashed #ccc',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        backgroundColor: '#f9f9f9',
                    }}>
                        {preview || currentLogo ? (
                            <img
                                src={preview || currentLogo}
                                alt="org logo"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Typography variant="body2" color="textSecondary">No logo</Typography>
                        )}
                    </div>

                    <input
                        ref={inputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />

                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => inputRef.current.click()}
                    >
                        {file ? 'Change Image' : 'Choose Image'}
                    </Button>

                    {file && (
                        <Typography variant="caption" color="textSecondary">
                            {file.name}
                        </Typography>
                    )}

                    {error && (
                        <Typography variant="body2" style={{ color: 'red' }}>
                            {error}
                        </Typography>
                    )}
                </div>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 16px' }}>
                <Button onClick={handleClose} variant="contained">
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    color="secondary"
                    variant="contained"
                    disabled={!file || loading}
                >
                    {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : 'Upload'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
